/**
 * "Split by" picker — ONE branded checkbox-list control shared by the left rail and the
 * analytics panel (both bind the same lifted `splits` state in SiteMapApp, so they can never
 * disagree). Replaces the rail's old native <select>.
 *
 * Ordered multi-level semantics (toggleSplit, pure + tested):
 *   · checking a lens APPENDS it as the deepest layer — its row gets a numbered badge (① ②);
 *   · unchecking removes the layer and PROMOTES deeper layers up;
 *   · checking another lens while at MAX_SPLITS replaces the deepest layer (the trigger
 *     tooltip explains this when the stack is full);
 *   · the "Clear splits" row on top resets the whole stack.
 * Rows use keepOpen so building a stack never reopens the menu; the popover is portaled
 * (ui/Menu) so it can't be clipped by the rail or the panel. The trigger reads the layer
 * path ("Model › Phase") and wears the accent ring while any split is active.
 */
import React from 'react';
import { LENSES, LENS_GROUPS, lensLabel, type LensId } from '../data/lenses';
import { MAX_SPLITS, toggleSplit } from '../data/splitPref';
import { T } from '../theme/tokens';
import { Menu, type MenuSection } from './Menu';

const BADGES = ['①', '②', '③', '④', '⑤'];

export function SplitPicker({ lens, splits, setSplits, fieldNames, triggerStyle, block }: {
  lens: LensId;
  splits: LensId[];
  setSplits: (s: LensId[]) => void;
  /** live backend field names — rail passes them so labels match the Color-By picker */
  fieldNames?: Record<string, string>;
  triggerStyle?: React.CSSProperties;
  /** full-width rail-style trigger */
  block?: boolean;
}) {
  const name = (id: LensId) => lensLabel(LENSES[id], fieldNames);
  const label = splits.length ? splits.map(name).join(' › ') : 'None';
  const atMax = splits.length >= MAX_SPLITS;
  const sections: MenuSection[] = [
    {
      items: [{
        key: '__none', label: splits.length ? `Clear splits (${splits.length})` : 'None',
        checked: splits.length === 0, onSelect: () => setSplits([]),
      }],
    },
    ...LENS_GROUPS.map((g): MenuSection => ({
      title: g.label,
      items: g.ids.filter(id => id !== lens).map(id => {
        const at = splits.indexOf(id);
        return {
          key: id, checkbox: true, keepOpen: true, checked: at >= 0,
          label: name(id),
          // layer-order badge — ① is the first sub-grouping under every row, ② nests under ①
          hint: at >= 0
            ? <span aria-label={`layer ${at + 1}`} style={{ color: T.accent, fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{BADGES[at] || at + 1}</span>
            : undefined,
          onSelect: () => setSplits(toggleSplit(splits, id)),
        };
      }),
    })).filter(s => s.items.length > 0),
  ];
  return (
    <Menu
      ariaLabel={`Split each row by up to ${MAX_SPLITS} more lenses, in order`}
      active={splits.length > 0} minWidth={232} block={block} triggerStyle={triggerStyle}
      title={atMax
        ? `Layers: ${label}. Stack is full — checking another lens replaces layer ${MAX_SPLITS}; uncheck a layer to remove it (deeper layers move up).`
        : `Break every row down by up to ${MAX_SPLITS} more lenses, in order — e.g. Model rows split by Phase, then each of those by Package`}
      label={label}
      sections={sections}
    />
  );
}
